import { motion } from "framer-motion";
import { Plane, Clock, MapPin, Shield, Luggage, Navigation, Car, ArrowRight, IndianRupee, Video, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const ServicesSection = ({ onBookAirport, onJoinWaitlist }) => {
  const airportFeatures = [
    { icon: IndianRupee, title: "Fixed Fare" },
    { icon: TrendingDown, title: "No Surge" },
    { icon: Clock, title: "On-Time Pickup" },
    { icon: Shield, title: "Flight Tracking" },
    { icon: Video, title: "DashCam" },
  ];

  const cityFeatures = [
    { icon: IndianRupee, title: "Fixed Fare" },
    { icon: Shield, title: "No surge" },
    { icon: Video, title: "DashCam" },
  ];

  return (
    <section id="services" className="py-16 sm:py-20 bg-sarathi-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sarathi-primary font-semibold text-xs tracking-widest uppercase">
            Our Services
          </span>
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold tracking-tight text-white mt-3 mb-3">
            What We Offer
          </h2>
          <p className="text-sarathi-text-secondary text-sm max-w-xl mx-auto">
            Premium EV rides for every journey<span className="hidden sm:inline"> — airport transfers and city rides.</span>
            <span className="sm:hidden"><br />— airport transfers and city rides.</span>
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Airport Transfers Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex"
          >
            <div className="bg-sarathi-surface border border-white/10 rounded-2xl p-6 hover:border-sarathi-primary/40 transition-all duration-300 flex flex-col w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-sarathi-primary/20 rounded-xl flex items-center justify-center shrink-0">
                  <Plane className="w-5.5 h-5.5 text-sarathi-primary" />
                </div>
                <div>
                  <h3 className="font-outfit text-lg font-bold text-white">Airport Transfers</h3>
                  <p className="text-sarathi-text-secondary text-xs">To & from Kempegowda International Airport</p>
                </div>
              </div>

              <p className="text-sarathi-text-muted text-sm mb-5">
                From your door to the departure gate —<br />unhurried, unworried, unstressed.
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {airportFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-1.5 bg-sarathi-background/60 border border-white/8 rounded-full px-3 py-1.5"
                  >
                    <feature.icon className="w-3 h-3 text-sarathi-primary" strokeWidth={2} />
                    <span className="text-white text-xs font-medium">{feature.title}</span>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-auto">
                <Button
                  onClick={onBookAirport}
                  className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white rounded-full px-7 h-9 text-sm group"
                  data-testid="service-airport-btn"
                >
                  Book Airport Transfer
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </motion.div>

          {/* City Rides Card — Coming Soon */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex"
          >
            <div className="relative bg-sarathi-surface border border-white/10 rounded-2xl p-6 transition-all duration-300 flex flex-col w-full overflow-hidden">
              {/* Coming Soon overlay — diagonal corner ribbon */}
              <div className="absolute top-4 right-4 z-10">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500 text-black text-[10px] font-bold tracking-wider uppercase shadow-lg" data-testid="coming-soon-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-black/70 animate-pulse" />
                  Coming Soon
                </span>
              </div>

              {/* Content (greyed/dimmed) */}
              <div className="opacity-60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-sarathi-primary/20 rounded-xl flex items-center justify-center shrink-0">
                    <Car className="w-5.5 h-5.5 text-sarathi-primary" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-bold text-white whitespace-nowrap">City Rides</h3>
                    <p className="text-sarathi-text-secondary text-xs">Within Bengaluru</p>
                  </div>
                </div>

                <p className="text-sarathi-text-muted text-sm mb-5">
                  Premium in-city rides, pre-scheduled and on your terms.
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {cityFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-1.5 bg-sarathi-background/60 border border-white/8 rounded-full px-3 py-1.5"
                    >
                      <feature.icon className="w-3 h-3 text-sarathi-primary" strokeWidth={2} />
                      <span className="text-white text-xs font-medium">{feature.title}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Waitlist CTA */}
              <div className="text-center mt-auto">
                <Button
                  onClick={onJoinWaitlist}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full px-7 h-9 text-sm group"
                  data-testid="service-waitlist-btn"
                >
                  Join Waitlist
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
